import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../contexts/SessionContext';
import API_URL from '../constants/environment';
import { OrderDetailResponse, SelectionPart, SelectionStatus } from './useGetOrderDetail';
import { invalidateOrder } from './invalidateOrder';

interface ConfirmAllSelectionsInput {
    orderId: string;
}

const confirmAllSelections = async (data: ConfirmAllSelectionsInput, sessionId: string | null) => {
    return axios.patch(`${API_URL}/order/part/confirm-all`, { orderId: data.orderId }, {
        headers: {
            Authorization: sessionId
        }
    });
};

export const useConfirmAllSelections = () => {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: ConfirmAllSelectionsInput) => confirmAllSelections(data, sessionContext?.sessionId ?? null),
        onMutate: async ({ orderId }: ConfirmAllSelectionsInput) => {
            await queryClient.cancelQueries({ queryKey: ['/order/detail', orderId] });

            const previousOrder = queryClient.getQueryData<OrderDetailResponse>(['/order/detail', orderId]);

            queryClient.setQueryData(['/order/detail', orderId], (old: OrderDetailResponse | undefined): OrderDetailResponse | undefined => {
                if (!old || !old.selection) return old;

                const updatePart = (part: SelectionPart): SelectionPart => {
                    if (part.selectionStatus === 'partbay' || part.selectionStatus === 'flagged') {
                        return { ...part, selectionStatus: 'manual' as SelectionStatus };
                    }
                    return part;
                };

                return {
                    ...old,
                    selection: old.selection.map((tab) => ({
                        ...tab,
                        parts: tab.parts.map(updatePart),
                    })),
                };
            });

            return { previousOrder };
        },
        onError: (_error, { orderId }, context) => {
            if (context?.previousOrder) {
                queryClient.setQueryData(['/order/detail', orderId], context.previousOrder);
            }
        },
        onSuccess: (_data, { orderId }) => {
            invalidateOrder(queryClient, orderId);
        },
    });
};
